/**
 * 告警智能分析 Mock 数据
 * 用于前端开发阶段，后端接口就绪后可移除
 */

/**
 * 模拟告警快速分析结果
 * @param {Object} context 告警上下文
 * @returns {Promise<Object>} 分析结果
 */
export function mockAlarmQuickAnalysis(context) {
  // 定义分析步骤
  const steps = [
    { text: '读取当前告警详情', duration: 500 },
    { text: '关联同设备近 7 天告警记录', duration: 800 },
    { text: '对比告警前后温度、电压、电流、SOC 曲线', duration: 1000 },
    { text: '分析同组设备运行状态', duration: 600 },
    { text: '输出可能原因、影响判断和建议关注的数据项', duration: 400 }
  ]

  return new Promise((resolve) => {
    const alarmContext = context.context || {}

    // 根据告警等级生成不同的影响判断
    const getImpactByLevel = (level) => {
      if (level >= 3) {
        return {
          level: 'high',
          description: '疑似影响晚高峰放电收益，建议立即处理。停机期间约损失放电收益 1,200 元，可能影响电站整体运行效率。'
        }
      } else if (level === 2) {
        return {
          level: 'medium',
          description: '对电站运行有一定影响，建议在 2 小时内处理。预计影响收益约 500 元。'
        }
      } else {
        return {
          level: 'low',
          description: '影响较小，建议在日常巡检时关注。暂未发现明显收益损失。'
        }
      }
    }

    const result = {
      code: 200,
      message: 'success',
      data: {
        summary: `该设备在24小时内连续${alarmContext.trigger_count || 1}次出现${alarmContext.alarm_type || '告警'}。告警发生在${alarmContext.start_time || '未知时间'}，${alarmContext.end_time ? `持续约${calculateDuration(alarmContext.start_time, alarmContext.end_time)}` : '持续中'}。初步判断可能与温控系统异常相关，同组设备未出现类似情况。`,

        // 返回步骤定义，前端逐步展示
        process: steps.map(s => s.text),
        processSteps: steps, // 包含时长信息

        impact: getImpactByLevel(alarmContext.alarm_level || 1),

        history: [
          {
            id: 'h1',
            text: `24小时内重复 ${alarmContext.trigger_count || 1} 次`,
            count: alarmContext.trigger_count || 1,
            timeRange: '2026-06-29 ~ 2026-06-30'
          },
          {
            id: 'h2',
            text: '近7天同设备同类告警趋势上升',
            trend: 'up'
          },
          {
            id: 'h3',
            text: '该设备历史上共发生过 15 次类似告警',
            count: 15
          }
        ],

        suggestions: [
          '建议优先核查温控系统运行状态',
          '查看电池簇温度传感器数据是否异常',
          '对比同组 eBlock 运行数据，排查是否为单点故障',
          '查看告警前后的 SOC 和温度曲线变化趋势',
          '如问题持续，建议联系设备厂商进行深度诊断'
        ],

        timestamp: new Date().toISOString()
      }
    }

    // 计算总时长
    const totalDuration = steps.reduce((sum, step) => sum + step.duration, 0)

    setTimeout(() => {
      resolve(result)
    }, totalDuration)
  })
}

/**
 * 计算时间持续时长
 */
function calculateDuration(startTime, endTime) {
  if (!startTime || !endTime) return '未知时长'

  const start = new Date(startTime)
  const end = new Date(endTime)
  const diff = end - start

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  if (hours > 0) {
    return `${hours} 小时 ${minutes} 分钟`
  } else {
    return `${minutes} 分钟`
  }
}

/**
 * 模拟不同场景的分析结果
 */
export const mockScenarios = {
  // 高等级告警
  highLevel: {
    code: 200,
    data: {
      summary: '设备出现严重故障，24小时内连续触发 5 次高等级告警。初步判断为电池温度异常导致的保护性停机，存在安全隐患。',
      impact: {
        level: 'critical',
        description: '严重影响电站运行安全和收益。建议立即停机检查，预计损失收益 3,500 元/天。'
      },
      history: [
        { id: 'h1', text: '24小时内重复 5 次', count: 5 },
        { id: 'h2', text: '近7天同设备告警频率异常', trend: 'up' },
        { id: 'h3', text: '历史同类告警 23 次，问题反复出现', count: 23 }
      ],
      suggestions: [
        '立即停机，联系技术人员现场排查',
        '检查电池管理系统（BMS）工作状态',
        '核查温控系统是否正常运行',
        '检查电池簇是否存在物理损坏',
        '建议更换故障传感器或电池模组'
      ]
    }
  },

  // 低等级告警
  lowLevel: {
    code: 200,
    data: {
      summary: '设备出现轻微异常提示，属于正常运行范围内的波动。建议定期观察，暂无需特殊处理。',
      impact: {
        level: 'low',
        description: '对电站运行影响较小，可在日常巡检时关注。暂未发现收益损失。'
      },
      history: [
        { id: 'h1', text: '24小时内首次出现', count: 1 },
        { id: 'h2', text: '近7天同设备运行稳定', trend: 'stable' }
      ],
      suggestions: [
        '定期查看设备运行数据',
        '如频繁出现，建议深度排查',
        '保持日常维护计划'
      ]
    }
  },

  // API 错误
  error: {
    code: 500,
    message: '智能分析服务暂时不可用，请稍后重试'
  }
}

export default {
  mockAlarmQuickAnalysis,
  mockScenarios
}
